import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Confirm from "../../../components/Confirm";
import Popup from "../../../components/Popup";
import {
  ActionsCell,
  Badge,
  ClickableRow,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  Segmented,
  TableSkeleton,
} from "../../../components/ui";
import {
  deleteUser,
  getUser,
  getUserListByPage,
  getUserListByPageWithSearch,
  getUserListCount,
  getUsersFreeCount,
  getUsersActivityCounts,
  getActiveUsersEvolution,
  giveUserSubscription,
  revokeUserSubscription,
  updateUserDetails,
  userSearchQuery,
} from "../../../services/users";
import { getUserInfo } from "../../../store/actions/uploadMediaAction";
import { getDate } from "../../../utils/format";
import { formatChange, formatNumber } from "../../../utils/numbers";
import { AccountStatusBadge, ActivityBadge, ACTIVE_WITHIN_DAYS, activityOf } from "./accountBadges";
import WinbackComposer, { WinbackProgress } from "./WinbackComposer";
import { getWinbackJob } from "../../../services/winback";
import { can } from "../../../utils/roles";
import "./user-list.css";

const PAGE_SIZE = 10;

// Hook personnalisé pour la gestion des abonnements admin
export const useAdminSubscription = () => {
  const [loading, setLoading] = useState(false);

  const giveSubscription = async (
    userId,
    subscriptionType = "paid",
    months = 1
  ) => {
    setLoading(true);
    try {
      // Passage en gratuit : retrait réel de l'abonnement (accès coupé, prélèvement Stripe arrêté)
      if (subscriptionType === "free") {
        const data = await revokeUserSubscription(userId);
        toast.success(data.message || "Abonnement retiré");
        return data;
      }
      if (subscriptionType === "paid") {
        subscriptionType = "monthly"; // Par défaut, définir sur mensuel si payé
      }
      const data = await giveUserSubscription(userId, subscriptionType, months);
      toast.success(data?.message || "Abonnement modifié avec succès");
      return data;
    } catch (error) {
      toast.error(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { giveSubscription, loading };
};

/**
 * Server-side filters: search (email, phone, last name ("first last") or first name)
 * and app activity ("active" | "inactive" | "never", "all" = no filter).
 */
const searchQuery = (search, activity = "all") => {
  const query = userSearchQuery(search);
  if (activity === "all") return query;
  const activityParams = `activity=${activity}&days=${ACTIVE_WITHIN_DAYS}`;
  return query ? `${query}&${activityParams}` : `?${activityParams}`;
};

const fullName = (user) =>
  [user?.name, user?.last_name].filter(Boolean).join(" ") || "N/A";

const UserList = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { giveSubscription } = useAdminSubscription();

  const [counts, setCounts] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activity, setActivity] = useState("all");
  // { type: "subscription" | "status" | "delete", user }
  const [pendingAction, setPendingAction] = useState(null);
  // « Vous nous avez manqué » : { user } (individuel) ou {} (groupé)
  const [winback, setWinback] = useState(null);
  const [winbackJob, setWinbackJob] = useState(null);
  const canEmail = can("users", "edit");

  // Envoi groupé en cours (lancé ici ou par un autre admin) : avancement affiché
  useEffect(() => {
    getWinbackJob().then((job) => setWinbackJob(job && !job.finishedAt ? job : null));
  }, []);

  useEffect(() => {
    async function fetchCounts() {
      const total = await getUserListCount();
      const free = await getUsersFreeCount();
      const activity = await getUsersActivityCounts(ACTIVE_WITHIN_DAYS);
      const evolution = await getActiveUsersEvolution(ACTIVE_WITHIN_DAYS);
      setCounts({
        total,
        free,
        paid: total - free,
        active: Number.isFinite(activity?.active) ? activity.active : null,
        // Évolution en % vs les 30 jours précédents, seulement si l'historique les couvre
        activeChange: evolution?.complete ? evolution.change : null,
      });
    }
    fetchCounts();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const changeActivity = (value) => {
    setActivity(value);
    setPage(1);
  };

  const loadPage = useCallback(async () => {
    setLoading(true);
    const query = searchQuery(debouncedSearch, activity);
    const data = query
      ? await getUserListByPageWithSearch(page, query)
      : await getUserListByPage(page);
    setUsers(Array.isArray(data) ? (query ? data : [...data].reverse()) : []);
    setLoading(false);
  }, [page, debouncedSearch, activity]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleView = (userId) => {
    getUser(userId).then((data) => {
      dispatch(getUserInfo(data));
      history.push("/dashboard/user-info");
    });
  };

  const changeSubscription = async (user) => {
    try {
      await giveSubscription(
        user._id,
        user.subscription === "free" ? "paid" : "free",
        1
      );
      await loadPage();
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  const changeStatus = (user) => {
    const status = user.status !== true;
    updateUserDetails({ _id: user._id, status }).then(() => {
      toast.success(
        status ? t("Account enabled") : t("Account disabled"),
        {
          progressStyle: { background: "var(--iq-primary)" },
        }
      );
      loadPage();
    });
  };

  const removeUser = async (user) => {
    try {
      await deleteUser(user._id);
      toast.success(t("User Deleted Successfully!"), {
        progressStyle: { background: "var(--iq-primary)" },
      });
      await loadPage();
    } catch (error) {
      console.log(error);
    }
  };

  const isSearching = Boolean(searchQuery(debouncedSearch, activity));
  const pageCount = Math.max(1, Math.ceil((counts?.total || 0) / PAGE_SIZE));

  return (
    <Page>
      <PageHeader
        title={t("Users")}
        subtitle={t("Registered users of the platform")}
        actions={
          <>
          {canEmail && (
            <button type="button" className="ui-btn" onClick={() => setWinback({})}>
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              {t("Win back inactive users")}
            </button>
          )}
          <div className="users-counters">
            <span>
              <strong>{counts ? formatNumber(counts.total) : "…"}</strong>{" "}
              {t("users")}
            </span>
            <span>
              <strong>{counts ? formatNumber(counts.free) : "…"}</strong>{" "}
              {t("free users")}
            </span>
            <span>
              <strong>{counts ? formatNumber(counts.paid) : "…"}</strong>{" "}
              {t("paying users")}
            </span>
            {counts?.active != null && (
              <span title={t("Used the app in the last 30 days")}>
                <strong>{formatNumber(counts.active)}</strong>{" "}
                {t("active in 30 days")}
                {formatChange(counts.activeChange) && (
                  <em
                    className="users-counter-trend"
                    data-trend={
                      counts.activeChange > 0
                        ? "up"
                        : counts.activeChange < 0
                        ? "down"
                        : "flat"
                    }
                    title={t("vs the previous 30 days")}
                  >
                    {formatChange(counts.activeChange)}
                  </em>
                )}
              </span>
            )}
          </div>
          </>
        }
      />

      {winbackJob && (
        <section className="ui-card">
          <WinbackProgress job={winbackJob} onDone={() => setTimeout(() => setWinbackJob(null), 8000)} />
        </section>
      )}

      <section className="ui-card">
        <div className="ui-toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search by name, email or phone")}
          />
          <Segmented
            label={t("App activity")}
            value={activity}
            onChange={changeActivity}
            options={[
              { value: "all", label: t("All") },
              { value: "active", label: t("Active") },
              { value: "inactive", label: t("Inactive") },
              { value: "never", label: t("Never seen") },
            ]}
          />
        </div>

        {loading ? (
          <TableSkeleton />
        ) : users.length === 0 ? (
          <EmptyState icon="ri-user-search-line">
            {t("No Data Found")}
          </EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>{t("Account")}</th>
                  <th>{t("Registered")}</th>
                  <th>{t("Subscription")}</th>
                  <th>
                    {t("Account status")}
                    <span className="users-col-hint">{t("Set by an admin")}</span>
                  </th>
                  <th>
                    {t("App activity")}
                    <span className="users-col-hint">
                      {t("Active = seen in the last 30 days")}
                    </span>
                  </th>
                  <th className="ui-cell-actions">
                    <span className="sr-only">{t("action")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <ClickableRow key={user._id} onOpen={() => handleView(user._id)}>
                    <td>
                      <div className="ui-media">
                        <span className="users-avatar" aria-hidden="true">
                          {(user.name || "?").charAt(0).toUpperCase()}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <span
                            className="ui-media-title"
                            title={fullName(user)}
                          >
                            {fullName(user)}
                          </span>
                          <span className="ui-media-meta">
                            {user.email || "N/A"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="ui-cell-muted">
                      {user.createdAt ? getDate(user.createdAt) : "N/A"}
                    </td>
                    <td>
                      {user.subscription === "free" ? (
                        <Badge color="slate">{t("Free")}</Badge>
                      ) : (
                        <Badge color="amber">{t("Paid")}</Badge>
                      )}
                    </td>
                    <td>
                      <AccountStatusBadge status={user.status} />
                    </td>
                    <td>
                      <ActivityBadge lastActiveAt={user.lastActiveAt} />
                    </td>
                    <ActionsCell>
                      <IconButton
                        icon="ri-eye-line"
                        label={t("View")}
                        onClick={() => handleView(user._id)}
                      />
                      {canEmail && user.email && activityOf(user.lastActiveAt) !== "active" && (
                        <IconButton
                          icon="ri-mail-send-line"
                          label={t("Send « We've missed you »")}
                          onClick={() => setWinback({ user })}
                        />
                      )}
                      <IconButton
                        icon="ri-vip-crown-line"
                        label={
                          user.subscription === "free"
                            ? t("Switch to paid")
                            : t("Switch to free")
                        }
                        onClick={() =>
                          setPendingAction({ type: "subscription", user })
                        }
                      />
                      <IconButton
                        icon={
                          user.status === true
                            ? "ri-forbid-line"
                            : "ri-shield-line"
                        }
                        label={
                          user.status === true
                            ? t("Disable account")
                            : t("Enable account")
                        }
                        onClick={() =>
                          setPendingAction({ type: "status", user })
                        }
                      />
                      <IconButton
                        icon="ri-delete-bin-line"
                        label={t("Delete")}
                        tone="danger"
                        onClick={() =>
                          setPendingAction({ type: "delete", user })
                        }
                      />
                    </ActionsCell>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <Pagination
            page={page}
            onChange={setPage}
            pageSize={PAGE_SIZE}
            {...(isSearching
              ? { hasNext: users.length === PAGE_SIZE }
              : {
                  pageCount,
                  total: counts?.total ?? undefined,
                  hasNext: page < pageCount,
                })}
          />
        )}
      </section>

      {pendingAction?.type === "subscription" && (
        <Confirm
          open={true}
          setOpen={() => setPendingAction(null)}
          confirmHandle={() => changeSubscription(pendingAction.user)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to change the Subscription?")}
          </div>
        </Confirm>
      )}
      {pendingAction?.type === "status" && (
        <Confirm
          open={true}
          setOpen={() => setPendingAction(null)}
          confirmHandle={() => changeStatus(pendingAction.user)}
        >
          <div className="model-wrapper">
            {pendingAction.user.status === true
              ? t("Disable the account of this user?")
              : t("Enable the account of this user?")}
          </div>
        </Confirm>
      )}
      {pendingAction?.type === "delete" && (
        <Popup
          open={true}
          setOpen={() => setPendingAction(null)}
          deleteHandle={() => removeUser(pendingAction.user)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete")}{" "}
            {fullName(pendingAction.user)}?
          </div>
        </Popup>
      )}
      {winback && (
        <WinbackComposer
          user={winback.user}
          onClose={() => setWinback(null)}
          onSent={(result) => {
            if (winback.user) setWinback(null);
            else if (result?.finishedAt) setWinbackJob(null);
          }}
        />
      )}
    </Page>
  );
};

export default UserList;
